<template>
  <div>
    <h2 class="primary darken-2 white--text font-weight-bold subheading pt-1 text-xs-center">
      Business Transactions
    </h2>
    <v-divider></v-divider>

    <!-- Filter Card -->
    <v-card class="filter-card elevation-2">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="5">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              type="date"
              :max="endDate || today"
              outlined
              dense
              prepend-icon="mdi-calendar-start"
              color="teal"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="5">
            <v-text-field
              v-model="endDate"
              label="End Date"
              type="date"
              :min="startDate || today"
              :max="today"
              outlined
              dense
              prepend-icon="mdi-calendar-end"
              color="teal"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="2" class="text-center">
            <v-btn 
              color="teal darken-2" 
              fab 
              dark
              @click="fetchTransactions"
            >
            <i class="fas fa-search white--text"></i>   
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Transactions List -->
    <v-card class="elevation-2 mt-4">
      <v-card-title class="teal--text darken-2">
        <v-icon left>mdi-history</v-icon>
        {{ totalTransactions }} transactions found
      </v-card-title>

      <div class="" style="max-height: 400px; overflow: auto">
        <v-card
          color="white"
          v-for="transaction in transactions"
          :key="transaction.id"
          class="mb-1"
        >
          <v-card-text class="px-3 py-2" style="font-size: 12px; cursor: pointer">
            <h2 class="teal--text">
              Type: 
              <span :class="txTypeColorClass(transaction.type)">
                {{ txTypeLabel(transaction.type) }}
              </span>
            </h2>
            
            <div class="mt-1">
              <b>Amount:</b> {{ transaction.amount }} FCFA
            </div>
            
            <div class="mt-1">
              <b>Date:</b> {{ formatDate(transaction.date) }} at {{ transaction.time }}
            </div>
            
            <v-spacer></v-spacer>
            
            <div class="mt-1">
              <span v-if="transaction.payment_method">
                <b>Method:</b> 
                {{ transaction.payment_method }} 
                <span v-if="transaction.user_phone">({{ transaction.user_phone }})</span>
              </span>
            </div>
            <div class="mt-1">
              <span v-if="transaction.user_email">
                <b>Email:</b> {{ transaction.user_email }}
              </span>
            </div>
           

            <div class="mt-1">
              <span v-if="transaction.user_name">
                <b>Name:</b> {{ transaction.user_name }}
              </span>
              <span v-if="transaction.description" class="mt-1">
                <b>Description:</b> {{ transaction.description }}
              </span>
            </div>
            <div class="mt-1">
              <span v-if="transaction.reference">
                <b>ref:</b> {{ transaction.reference }}
              </span>
            </div>
            
            <div class="mt-1">
              <b>Status:</b> 
              <span :class="statusColorClass(transaction.status)">
                {{ txStatus(transaction.status) }}
              </span>
            </div>
          </v-card-text>
          <v-divider></v-divider>
        </v-card>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data: () => ({
    today: new Date().toISOString().substr(0, 10),
    startDate: null,
    endDate: null,
    currentPage: 1,
    totalPages: 1,
    totalTransactions: 0,
    transactions: []
  }),

  computed: {
    category() {
      return this.$route.params.category || 'all'
    }
  },

  watch: {
    category() {
      this.fetchTransactions()
    }
  },

  mounted() {

    this.fetchTransactions()
  },

  methods: {
    initializeDates() {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      this.startDate = start.toISOString().substr(0, 10)
      this.endDate = end.toISOString().substr(0, 10)
    },

    async fetchTransactions() {
      try {
        const { data } = await axios.get(
          `/api/v1/home/get/user/transactions/${this.startDate}/${this.endDate}/${this.category}/`,
          { params: { page: this.currentPage } }
        )

        this.transactions = data.results
        this.totalTransactions = data.total
        this.totalPages = Math.ceil(data.total / data.page_size)
        
      } catch (error) {
        this.$store.commit('setSnackbar', {
          message: 'Error loading transactions',
          color: 'error',
          active: true
        })
      }
    },

    txTypeLabel(type) {
      return {
        DEPOSIT: 'Deposit',
        WITHDRAWAL: 'Withdrawal',
        COMMISSION: 'Commission'
      }[type] || 'Transaction'
    },

    txTypeIcon(type) {
      return {
        DEPOSIT: 'mdi-arrow-down',
        WITHDRAWAL: 'mdi-arrow-up',
        COMMISSION: 'mdi-account-cash'
      }[type] || 'mdi-cash'
    },

    txTypeColor(type) {
      return {
        DEPOSIT: 'green',
        WITHDRAWAL: 'red',
        COMMISSION: 'blue'
      }[type] || 'grey'
    },

    txStatus(status) {
      return {
        PENDING: 'Pending',
        COMPLETED: 'Completed',
        CANCELLED: 'Cancelled'
      }[status] || status
    },

    statusColor(status) {
      return {
        PENDING: 'orange',
        COMPLETED: 'green',
        CANCELLED: 'red'
      }[status] || 'grey'
    },

    formatDate(dateStr) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return new Date(dateStr).toLocaleDateString('fr-FR', options)
    },

    txTypeColorClass(type) {
      return {
        DEPOSIT: 'green--text',
        WITHDRAWAL: 'red--text',
        COMMISSION: 'blue--text'
      }[type] || 'grey--text'
    },

    statusColorClass(status) {
      return {
        PENDING: 'blue--text',
        COMPLETED: 'success--text',
        CANCELLED: 'red--text'
      }[status] || 'grey--text'
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
}

.teal--text {
  font-size: 14px;
  margin-bottom: 8px;
}

.mt-1 {
  margin-top: 4px;
}
</style>
